import React, { useState, useEffect, useMemo } from "react";
import _ from "lodash";
import {
  TextInput,
  Select,
  Textarea,
  NumberInput,
  Group,
  Title,
  Text,
  Divider,
  Button,
  Autocomplete,
} from "@mantine/core";
import ReactJson from "react-json-view";

import ArrayTable from "./arrayTable";
import { DATA_CURRENCY, DATA_GI_CHARGE_ITEMS } from "../data/options";
import { useFormatter } from "../hooks/useFomatter";
import { useForceUpdate } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import { useServerApi } from "../hooks/userServerApi";
import RichTextEditor from "./richTextEditor";
import FormReadOnlyField from "./formReadOnlyField";
import { isWidgetReadOnly } from "../helpers/formReadOnly";
import { formatFormFieldDisplay } from "../helpers/formatFormFieldDisplay";

const calculateTotal = (charges) => {
  if (_.isEmpty(charges)) return 0;
  const total = charges.reduce((a, c) => {
    const lineAmount = (c.qty ?? 1) * c.unitPrice;
    return a + +lineAmount.toFixed(2);
  }, 0);
  return +total.toFixed(2);
};

/** API / inputs may store costTotal as string; compare numerically to avoid setFieldValue every render. */
const costTotalsMatch = (cur, computedTotal) => {
  const toNum = (v) => {
    if (v == null || v === "") return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : NaN;
  };
  const a = toNum(cur);
  const b = toNum(computedTotal);
  if (Number.isFinite(a) && Number.isFinite(b)) {
    return Math.abs(a - b) < 0.005;
  }
  return cur === computedTotal;
};

const DebitNoteCostItems = ({
  form,
  name,
  allowGetRate = false,
  readonly = false,
  readOnly = false,
  disabled = false,
}) => {
  const costItems = _.get(form.values, "costItems");
  const chargeItems = _.get(form.values, "chargeItems");
  const paymentStatus = _.get(form.values, "paymentStatus");
  const PAYMENT_STATUS_TO_LOCK =
    process.env.REACT_APP_LOCK_DN_PAYMENT_STATUS?.split(",") ?? [];
  const allowToEdit = !PAYMENT_STATUS_TO_LOCK.includes(paymentStatus);
  const fieldsLocked =
    isWidgetReadOnly({ disabled, readOnly, readonly, form }) || !allowToEdit;
  // const commissionRate = _.get(form.values, "commissionRate")
  const forceUpdate = useForceUpdate();

  const showFixRate = process.env.REACT_APP_DEBIT_NOTE_SHOW_FIX_RATE === "true";

  const [api] = useServerApi();

  const total = useMemo(
    () => calculateTotal(Array.isArray(costItems) ? costItems : []),
    [costItems],
  );

  const [commissionRate, setCommissionRate] = useState(
    _.get(form.values, "commissionRate")
  );

  const [formatter] = useFormatter();

  const rowInitValue = {
    item: "",
    currency: "HKD",
    qty: 1,
    unitPrice: 0,
    price: 0,
  };

  const fields = [
    {
      header: "Item",
      headerWidth: "50%",
      fieldName: "item",
      component: Autocomplete,
      props: {
        data: DATA_GI_CHARGE_ITEMS,
        searchable: true,
        clearable: true,
      },
    },

    {
      header: "Qty",
      headerWidth: "10%",
      fieldName: "qty",
      component: NumberInput,
      props: {},
    },
    {
      header: "Currency",
      headerWidth: "12%",
      fieldName: "currency",
      component: Select,
      props: {
        data: DATA_CURRENCY,
        defaultValue: "HKD",
        searchable: true,
      },
    },
    {
      header: "Unit Price",
      headerWidth: "30%",
      fieldName: "unitPrice",
      component: NumberInput,
      props: {
        parser: formatter.currencyParser,
        formatter: formatter.currencyFormat,
        precision: 2,
      },
    },
  ];

  useEffect(() => {
    const cur = _.get(form.values, "costTotal");
    if (costTotalsMatch(cur, total)) return;
    form.setFieldValue("costTotal", total);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- omit form; stable setFieldValue
  }, [total]);

  //Just for GI , just get the first row.
  const getCommissionRate = async (id) => {
    if (!id) return;

    try {
      const table = await api.Product.getCurrentCommissionRateTable({
        id,
        date: form.values.date,
      });

      if (!table || !table.commissionVariation) return;

      const { rows } = table.commissionVariation;
      const row = rows[0];
      if (!row || !row.yr1) return;
      // console.log("getCommissionRate", rows, row.yr1);
      setCommissionRate(row.yr1);
      form.setFieldValue("commissionRate", row.yr1);

      forceUpdate();
      // console.log("getCommissionRate", row.yr1);
    } catch (error) {
      console.log(error);
    }
  };

  const applyCommissionRate = () => {
    let cr = _.get(form.values, "commissionRate");
    const chargeItems = _.get(form.values, "chargeItems");
    let newCostItems = _.cloneDeep(chargeItems);
    // newCostItems = newCostItems.filter((item) => item.unitPrice >= 0);
    const pItem = newCostItems.find((c) => c.item === "Premium");

    if (pItem) {
      let unitPrice = pItem.unitPrice * (1 - cr / 100);
      pItem.price = unitPrice * pItem.qty;
      pItem.unitPrice = unitPrice;
      pItem.item = "Net Premium";
    }

    // console.log("commissionRate", cr, newCostItems);

    form.setFieldValue("costItems", _.cloneDeep(newCostItems));
    form.setFieldValue("commissionRate", cr);
    // console.log("costItem", costItem, commissionRate);

    if (cr >= 0) {
      form.setFieldValue("appliedCommissionRate", true);
    }

    forceUpdate();
  };

  const applyFixRate = () => {
    // console.log("applyFixRate");
    const providerFixRate = _.get(form.values, "providerFixRate");
    const chargeItems = _.get(form.values, "chargeItems");
    const insuredValue = _.get(form.values, "insuredValue");
    let newCostItems = _.cloneDeep(chargeItems);
    newCostItems = newCostItems.filter((c) => c.item !== "Premium");

    //Add new Cost items to top
    const netPremiumValue = +(insuredValue * providerFixRate * 0.01).toFixed(2);
    newCostItems.unshift({
      item: "Net Premium",
      currency: "HKD",
      qty: 1,
      unitPrice: netPremiumValue,
      price: netPremiumValue,
    });

    form.setFieldValue("costItems", newCostItems);

    forceUpdate();
  };

  const { t } = useTranslation();
  return (
    <>
      {/* {_.get(form.values, "insuredValue")} */}
      {/* <ReactJson src={form.values.product} style={{ background: "white" }} /> */}
      {/* {_.get(form.values, "commissionRate")} */}
      <Group position="apart">
        <Group align={"end"} mb="xl">
          {fieldsLocked ? (
            <FormReadOnlyField
              label="Commission Rate(%)"
              value={formatFormFieldDisplay(_.get(form.values, "commissionRate"), {
                kind: "number",
                props: { precision: 4 },
              })}
            />
          ) : (
            <>
              <NumberInput
                precision={4}
                label="Commission Rate(%)"
                placeholder="30"
                value={_.get(form.values, "commissionRate")}
                onChange={(value) => {
                  setCommissionRate(value);
                  form.setFieldValue("commissionRate", value);
                }}
                size="xs"
              ></NumberInput>

              {allowGetRate && form.values.product && (
                <Button
                  size="xs"
                  onClick={() => getCommissionRate(form.values.product._id)}
                >
                  {t("Get Rate")}
                </Button>
              )}
              <Button
                size="xs"
                color="green"
                onClick={() => applyCommissionRate()}
              >
                {t("Apply Rate")}
              </Button>
            </>
          )}
        </Group>
        {showFixRate && (
          <Group align={"end"} mb="xl">
            {fieldsLocked ? (
              <FormReadOnlyField
                label="Provider Fix Rate(%)"
                value={formatFormFieldDisplay(
                  _.get(form.values, "providerFixRate"),
                  {
                    kind: "number",
                    props: { precision: 2 },
                  }
                )}
              />
            ) : (
              <>
                <NumberInput
                  precision={2}
                  label="Provider Fix Rate(%)"
                  placeholder="30"
                  value={_.get(form.values, "providerFixRate")}
                  onChange={(value) =>
                    form.setFieldValue("providerFixRate", value)
                  }
                  size="xs"
                  formatter={formatter.numberFormat}
                  parser={formatter.numberParser}
                ></NumberInput>
                <Button size="xs" color="green" onClick={() => applyFixRate()}>
                  Apply Fix Rate
                </Button>
              </>
            )}
          </Group>
        )}
      </Group>
      <ArrayTable
        form={form}
        name={name}
        rowInitValue={rowInitValue}
        fields={fields}
        readonly={fieldsLocked}
      />
      <Divider my="xl" />
      <Group position="right">
        <Group>
          <Title order={3}>{formatter.currency(total)}</Title>
        </Group>
      </Group>
      {/* <RichTextEditor
        form={form}
        name="providerCostReference"
        label="Please provide commission screenshot from provider"
        mt={"xl"}
      /> */}
    </>
  );
};

export default DebitNoteCostItems;
